import cx from 'clsx';
import React, { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';

import { ReactComponent as ViewDetailsIcon } from '@assets/svg/executive-summary/ic-view-details.svg';
import { ReactComponent as TableSortIcon } from '@assets/svg/table-sort.svg';

import TableEmptyRow from '@components/common/TableEmptyRow/TableEmptyRow';
import TooltipSort from '@components/common/TooltipSort/TooltipSort';

import { initialSorting } from '@constants/tableSorting';

import useTranslation from '@hooks/useTranslation';

import type { ITableSorting } from '@model/tableSorting';

import { useAssetTreeQuery } from '@services/useAssetTreeQuery';
import { useCustomerInfoQuery } from '@services/useCustomerInfoQuery';

import { selectAppLocationSelectValue } from '@store/useAppStore';

import { monthByMonthName } from '../../constants/monthByMonthName';
import { useActionItemsByMonthQuery } from '../../services/useActionItemsByMonthQuery';
import {
  selectCostSavingsIncludeUnverifiedSavings,
  selectCostSavingsYearFilter,
} from '../../store/useCostSavingsStore';
import ActionItemsByEquipmentTableSkeleton from '../ActionItemsByMonthTable/ActionItemsByMonthTableSkeleton';
import style from './style.module.scss';

interface IProps {
  monthName: string;
  total: number;
  isVisibleHiddenRow: boolean;
}

const ActionItemsByMonthTable: React.FC<IProps> = ({ monthName, total, isVisibleHiddenRow }) => {
  const [sorting, setSorting] = useState<ITableSorting>(initialSorting);

  const { t } = useTranslation();
  const { customerId } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const locationSelectValue = selectAppLocationSelectValue();

  const isSelectedAllFacility = locationSelectValue.id === 'all';

  const { data: customerInfoData } = useCustomerInfoQuery({
    customerId,
    locationIds:
      locationSelectValue.level_name === 'allFacilities' ? undefined : locationSelectValue?.filterIds,
  });
  const { data: assetTreeData } = useAssetTreeQuery();

  const currentCurrency = isSelectedAllFacility
    ? assetTreeData?.customerCurrency
    : locationSelectValue.currency;

  const yearFilter = selectCostSavingsYearFilter();
  const includeUnverifiedSavings = selectCostSavingsIncludeUnverifiedSavings();

  const { data, isLoading } = useActionItemsByMonthQuery({
    year: yearFilter.value,
    month: monthByMonthName[monthName as keyof typeof monthByMonthName],
    includeUnverifiedSavings,
    locationIds:
      locationSelectValue.level_name === 'allFacilities' ? undefined : locationSelectValue?.filterIds,
    sorting,
    enabled: isVisibleHiddenRow,
  });

  const onChangeSorting = (key: string) => {
    if (!sorting.id || sorting.id !== key) {
      setSorting({ id: key, desc: false });
      return;
    }
    const { id, desc } = sorting;
    if (desc) {
      setSorting(initialSorting);
    } else {
      setSorting({ id, desc: true });
    }
  };

  return (
    <td className={style.actionItemsByMonthTableWrapper} colSpan={6}>
      <table className={style.actionItemsByMonthTable}>
        <thead>
          <tr>
            <th>
              {t('Location')}
              <TableSortIcon
                className={cx(style.sortIcon, { [style.activeSort]: sorting.id === 'location_name' })}
                onClick={() => onChangeSorting('location_name')}
                data-tooltip-content="location_name"
                data-tooltip-id="ai-by-month-sort-tooltip"
              />
            </th>
            <th>
              {t('Equipment')}
              <TableSortIcon
                className={cx(style.sortIcon, { [style.activeSort]: sorting.id === 'equipment_name' })}
                onClick={() => onChangeSorting('equipment_name')}
                data-tooltip-content="equipment_name"
                data-tooltip-id="ai-by-month-sort-tooltip"
              />
            </th>
            <th>
              {t('Component')}
              <TableSortIcon
                className={cx(style.sortIcon, { [style.activeSort]: sorting.id === 'component_name' })}
                onClick={() => onChangeSorting('component_name')}
                data-tooltip-content="component_name"
                data-tooltip-id="ai-by-month-sort-tooltip"
              />
            </th>
            {customerInfoData?.showTotalCostSavings && (
              <th>
                {`${t('total savings')}, ${currentCurrency.symbol}`}
                <TableSortIcon
                  className={cx(style.sortIcon, { [style.activeSort]: sorting.id === 'cost_avoided' })}
                  onClick={() => onChangeSorting('cost_avoided')}
                  data-tooltip-content="cost_avoided"
                  data-tooltip-id="cost-savings-sort-tooltip"
                />
              </th>
            )}
            <th>
              {t('Action Item Status')}
              <TableSortIcon
                className={cx(style.sortIcon, { [style.activeSort]: sorting.id === 'status_name' })}
                onClick={() => onChangeSorting('status_name')}
                data-tooltip-content="status_name"
                data-tooltip-id="ai-by-month-sort-tooltip"
              />
            </th>
            <th />
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <ActionItemsByEquipmentTableSkeleton total={total} />
          ) : data?.length ? (
            data?.map((row) => (
              <tr className={style.bodyRow} key={row.id}>
                <td>
                  {row.location_name}
                  {locationSelectValue.id === 'all' && (
                    <div className={style.facilityName}>{row.facility_name}</div>
                  )}
                </td>
                <td>
                  <div
                    className={style.equipmentNameColumn}
                    data-tooltip-content={row.equipment_name}
                    data-tooltip-id="cell-tooltip"
                  >
                    {row.equipment_name}
                  </div>
                </td>
                <td>{row.component_name}</td>
                {customerInfoData?.showTotalCostSavings && (
                  <td>
                    {isSelectedAllFacility
                      ? row.cost_avoided_customer_currency
                      : row.cost_avoided_facility_currency}
                  </td>
                )}
                <td>{row.status_name === 'Archived' ? t('Verified') : t('Unverified')}</td>
                <td
                  className={style.iconColumn}
                  onClick={() =>
                    navigate(`/customer/${customerId}/ai/${row.id}`, {
                      state: {
                        prevPage: pathname,
                      },
                    })
                  }
                >
                  <ViewDetailsIcon className={style.viewDetailsIcon} id="view-action-item" />
                </td>
              </tr>
            ))
          ) : (
            <TableEmptyRow text={t('No action items were found.')} />
          )}
        </tbody>
      </table>
      <Tooltip anchorSelect="#view-action-item" content={t('View Action Item')} />
      <TooltipSort id="ai-by-month-sort-tooltip" sorting={sorting} />
      <Tooltip id="cell-tooltip" place="top-start" />
    </td>
  );
};

export default ActionItemsByMonthTable;
